"use client";

import { AddIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Link,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Fragment } from "react";
import { Field } from "../../../components/Field";
import { SERVER_STATE, useForm } from "../../../hooks/useForm";
import { NotFoundComponent } from "../../not-found-component";

import type { ContentEvent } from "../../content";

type EnrolData = { label: string; value: string };

const subscribeToNewsletter = async (email: string) => {
  const response = await fetch("/api/newsletter", {
    method: "put",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  const responseData = await response.json();
  if (!responseData.ok) {
    throw new Error(responseData.error);
  }
};

const sequentialEnrol = async (
  enrolDataPerPerson: EnrolData[][],
  event: ContentEvent,
) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const enrolData of enrolDataPerPerson) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(`/api/enrol`, {
      method: "put",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event: { id: event.id, title: event.title },
        enrolData,
      }),
    });
    // eslint-disable-next-line no-await-in-loop
    const responseData = await response.json();
    if (!responseData.ok) {
      throw new Error(responseData.error);
    }
  }
};

function EnrolForm(props: { event: ContentEvent }) {
  const form = useForm({
    formFieldsPerPerson: props.event.formFieldsPerPerson,
    generalFormFields: props.event.generalFormFields,
    onSubmit: async (state) => {
      const generalFormFields = state.generalFormFields.map((formField) => ({
        label: formField.label,
        value: formField.value,
      }));

      const emailField = generalFormFields.find(
        (field) => field.label === "Email-Adresse",
      );
      const newsletterField = generalFormFields.find(
        (field) => field.label === "Newsletter abbonieren",
      );
      const newsletterPromise =
        emailField && newsletterField && newsletterField.value === "Ja"
          ? subscribeToNewsletter(emailField.value)
          : Promise.resolve();

      const enrolDataPerPerson = state.formFieldsPerPerson.map((formFields) => [
        ...formFields.map((formField) => ({
          label: formField.label,
          value: formField.value,
        })),
        ...generalFormFields,
      ]);
      const emailPromises = enrolDataPerPerson.map(async (enrolData) => {
        const response = await fetch(`/api/mail/enrol`, {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event: { id: props.event.id, title: props.event.title },
            enrolData,
          }),
        });
        const responseData = await response.json();
        if (!responseData.ok) {
          throw new Error(responseData.error);
        }
      });

      await Promise.all([
        sequentialEnrol(enrolDataPerPerson, props.event),
        ...emailPromises,
        newsletterPromise,
      ]);
    },
  });

  return (
    <>
      <Text>
        Hier kannst du dich für das Event{" "}
        <Text as="strong">{props.event.title}</Text> anmelden. Gib einfach die
        geforderten Daten in das Formular unten ein und schon bist du dabei!
      </Text>
      {form.state.serverState === SERVER_STATE.SUCCESS ? (
        <Flex p={4} alignItems="center" rounded="md" bg="green.200" mt={4}>
          <CheckIcon />
          <Text ml={4}>
            Wir haben deine Anmeldung erhalten. Du solltest außerdem im Kürze
            eine Bestätigung per Email erhalten.
          </Text>
        </Flex>
      ) : null}
      {form.state.serverState === SERVER_STATE.ERROR ? (
        <Flex p={4} alignItems="center" rounded="md" bg="red.200" mt={4}>
          <CloseIcon />
          <Text ml={4}>
            Beim Absenden des Formulars ist ein unbekannter Fehler aufgetreten.
            Wir haben das zur Kenntnis genommen und werden uns schnellstmöglich
            darum kümmern. Bitte versuchen Sie es später noch einmal!
          </Text>
        </Flex>
      ) : null}
      {form.state.serverState === SERVER_STATE.SUBMITTING ? (
        <Flex justifyContent="center" mt={4}>
          <Spinner
            size="xl"
            speed="0.5s"
            thickness="4px"
            color="blue.500"
            emptyColor="gray.200"
          />
        </Flex>
      ) : null}
      {form.state.serverState === SERVER_STATE.NOT_SUBMITTED ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.submitForm();
          }}
        >
          {form.state.formFieldsPerPerson.map((formFields, personIndex) => (
            <Fragment key={personIndex}>
              <Flex mt={8} alignItems="center">
                <Heading as="h3" fontSize="lg">
                  Angaben zur Person
                  {form.state.formFieldsPerPerson.length === 1
                    ? ""
                    : ` ${personIndex + 1}`}
                </Heading>
                {form.state.formFieldsPerPerson.length > 1 ? (
                  <Button
                    leftIcon={<CloseIcon />}
                    onClick={() => {
                      form.removePerson(personIndex);
                    }}
                    ml={4}
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                  >
                    entfernen
                  </Button>
                ) : null}
              </Flex>
              {formFields.map((field) => (
                <Field
                  key={field.label}
                  personIndex={personIndex}
                  updateValue={(updateProperties) => {
                    form.updateValue({ ...updateProperties, personIndex });
                  }}
                  submitCount={form.state.submitCount}
                  {...field}
                />
              ))}
              {form.state.formFieldsPerPerson.length < 10 &&
              personIndex === form.state.formFieldsPerPerson.length - 1 ? (
                <Button
                  leftIcon={<AddIcon />}
                  mt={6}
                  onClick={form.addPerson}
                  variant="outline"
                  colorScheme="blue"
                >
                  weitere Person anmelden
                </Button>
              ) : null}
            </Fragment>
          ))}
          <Heading as="h3" fontSize="lg" mt={8}>
            Weitere Angaben
          </Heading>
          {form.state.generalFormFields.map((field) => (
            <Field
              key={field.label}
              updateValue={form.updateValue}
              submitCount={form.state.submitCount}
              {...field}
            />
          ))}
          <Button colorScheme="blue" type="submit" mt={6}>
            absenden
          </Button>
          <FormControl
            isInvalid={form.state.submitCount > 0 && !form.state.isValid}
          >
            <FormErrorMessage>
              Das Formular enthält noch Fehler!
            </FormErrorMessage>
          </FormControl>
          <Text fontSize="sm" mt={4}>
            Mit dem Abschicken dieses Formulars bestätige ich, dass ich die{" "}
            <Link isExternal color="blue.500" href="/info/teilnahmebedingungen">
              allgemeinen Teilnahmebedingungen
            </Link>{" "}
            und alle{" "}
            <Link
              isExternal
              color="blue.500"
              href={`/termine/${props.event.id}`}
            >
              weiteren Information
            </Link>
            , die dieses Event betreffen, gelesen habe und alle dort genannten
            Bedingungen akzeptiere. Das gilt insbesondere auch für die{" "}
            <Link isExternal color="blue.500" href="/info/lastschrift">
              Information bezüglich Lastschriftmandat und Bezahlung
            </Link>{" "}
            sowie unsere{" "}
            <Link isExternal color="blue.500" href="/info/datenschutz">
              Hinweise zum Datenschutz
            </Link>
            .
          </Text>
        </form>
      ) : null}
    </>
  );
}

type Props = {
  event: ContentEvent | null;
};

export function PageComponent(props: Props) {
  const { event } = props;
  return event ? (
    <>
      <Heading mb={4}>Anmeldeformular</Heading>
      {event.hasEnrol ? null : (
        <Text>
          Für das Event <Text as="strong">{event.title}</Text> kann man sich
          leider nicht online anmelden!
        </Text>
      )}
      {event.hasEnrol && event.status === "limit reached" ? (
        <Text>
          Das Limit für die Anzahl der Anmeldungen für die Veranstaltung{" "}
          <Text as="strong">{event.title}</Text> ist schon erreicht. Die
          Online-Anmeldung ist daher leider nicht mehr möglich.
        </Text>
      ) : null}
      {event.hasEnrol && event.status === "canceled" ? (
        <Text>
          Wir müssen die Veranstaltung <Text as="strong">{event.title}</Text>{" "}
          auf Grund zu geringer Teilnehmeranzahl leider absagen. Die
          Online-Anmeldung ist daher nicht mehr möglich.
        </Text>
      ) : null}
      {event.hasEnrol &&
      !["limit reached", "canceled"].includes(event.status) ? (
        <EnrolForm key={event.id} event={event} />
      ) : null}
    </>
  ) : (
    <NotFoundComponent />
  );
}
